/*
This file contains only logic used for the bottom menu
There should be no dependencies on react or hooks here
This will make it easier to test the functionalities
*/

import { IDashboardContext } from "../../contexts/dashboard-context";
import { isBackgroundColorPanelSupported } from "../bottom-background-menus/background-color-panel/menu-info";
import { isBackgroundPatternPanelSupported } from "../bottom-background-menus/background-pattern-panel/menu-info";
import { isBackgroundPanelSupported } from "../bottom-background-menus/menu-info";
import { isDesignsPanelSupported } from "../bottom-designs-menus/menu-info";
import { isTemplatesPanelSupported } from "../bottom-layouts-menu/menu-info";
import { isBottomRootMenuSupported } from "../bottom-menus/bottom-root-menu";
import { isPhotoEditingMenuSupported } from "../bottom-photo-menus/bottom-photo-editing/menu-info";
import { isBottomPhotoMenuSupported } from "../bottom-photo-menus/bottom-photo-menu/menu-info";
import { isPhotoEditingPanelSupported } from "../bottom-photo-menus/photo-editing-panel/menu-info";
import { isPhotoFiltersPanelSupported } from "../bottom-photo-menus/photo-filters-panel/menu-info";
import { isPhotoSelectionPanelSupported } from "../bottom-photo-menus/photo-selection-panel/menu-info";
import { isPhotoTransformSupported } from "../bottom-photo-menus/photo-transform-panel/menu-info";
import { isBottomTextMenuSupported } from "../bottom-text-menus/bottom-text-menu/menu-info";
import { isFontFamiliesPanelSupported } from "../bottom-text-menus/font-families-panel/menu-info";
import { isTextAlignmentPanelSupported } from "../bottom-text-menus/text-alignment-menu/menu-info";
import { isTextColorPanelSupported } from "../bottom-text-menus/text-color-panel/menu-info";
import { isTextSizePanelSupported } from "../bottom-text-menus/text-size-panel/menu-info";
import { isTextStylePanelSupported } from "../bottom-text-menus/text-style-panel/menu-info";
import { isTextValuePanelSupported } from "../bottom-text-menus/text-value-panel/menu-info";
import { isSymbolsPanelSupported } from "../bottom-menu-panels/symbols/menu-info";
import { MenuId, MenuIds } from "../../hooks/useMenus";

/**
 * Check if this menu can be shown
 */
function isSupportedMenu(
	menuId: MenuId,
	zone: eXpress.core.IZone | undefined,
	page: eXpress.core.IPageZone | null | undefined,
	dashboards: {
		main: IDashboardContext;
		text: eXpress.TextZoneDashboard;
	},
): boolean {
	const { main: dashboard } = dashboards;

	const requireTextDashboard = (): eXpress.TextZoneDashboard => {
		if (!dashboards.text) {
			throw new Error(`Menu ${menuId} requires a textzone dashboard`);
		}
		return dashboards.text;
	};

	switch (menuId) {
		case MenuIds.Root: {
			return isBottomRootMenuSupported();
		}
		case MenuIds.Photo: {
			return isBottomPhotoMenuSupported(zone, dashboard, page);
		}
		case MenuIds.PhotoSelection: {
			return isPhotoSelectionPanelSupported(page);
		}
		case MenuIds.PhotoFilters: {
			return isPhotoFiltersPanelSupported(zone, dashboard);
		}
		case MenuIds.PhotoEditing: {
			return isPhotoEditingMenuSupported(zone, dashboard);
		}
		case MenuIds.PhotoTransform: {
			return isPhotoTransformSupported(zone, dashboard);
		}
		case MenuIds.PhotoBrightness:
		case MenuIds.PhotoContrast:
		case MenuIds.PhotoSaturation: {
			return isPhotoEditingPanelSupported(zone);
		}
		case MenuIds.Backgrounds: {
			return isBackgroundPanelSupported(page?.spread, dashboard);
		}
		case MenuIds.BackgroundColor: {
			return isBackgroundColorPanelSupported(page?.spread, dashboard);
		}
		case MenuIds.BackgroundPattern: {
			return isBackgroundPatternPanelSupported(page?.spread, dashboard);
		}
		case MenuIds.Layouts: {
			return isTemplatesPanelSupported(page, dashboard);
		}
		case MenuIds.Designs: {
			return isDesignsPanelSupported(page);
		}
		case MenuIds.Symbols: {
			return isSymbolsPanelSupported(zone, dashboard);
		}
		case MenuIds.TextColor: {
			const textDashboard = requireTextDashboard();
			return !!textDashboard && isTextColorPanelSupported(zone, textDashboard);
		}
		case MenuIds.FontFamilies: {
			const textDashboard = requireTextDashboard();
			return !!textDashboard && isFontFamiliesPanelSupported(zone, textDashboard);
		}
		case MenuIds.Text: {
			const textDashboard = requireTextDashboard();
			return !!textDashboard && isBottomTextMenuSupported(zone, page, textDashboard);
		}
		case MenuIds.TextValue: {
			const textDashboard = requireTextDashboard();
			return (
				!!textDashboard && isTextValuePanelSupported(zone, page?.definition, textDashboard)
			);
		}
		case MenuIds.TextSize: {
			const textDashboard = requireTextDashboard();
			return !!textDashboard && isTextSizePanelSupported(zone, textDashboard);
		}
		case MenuIds.TextStyle: {
			const textDashboard = requireTextDashboard();
			return (
				!!textDashboard && isTextStylePanelSupported(zone, page?.definition, textDashboard)
			);
		}
		case MenuIds.TextVAlignment:
		case MenuIds.TextHAlignment: {
			const textDashboard = requireTextDashboard();
			return (
				!!textDashboard &&
				isTextAlignmentPanelSupported(zone, page?.definition, textDashboard)
			);
		}

		// TODO? was not available in the past
		case MenuIds.Qr:
		case MenuIds.QrAdd:
		case MenuIds.QrEdit:
		case MenuIds.Map:
		case MenuIds.Options:
		case MenuIds.DateSelection:
		case MenuIds.ProductOptions:
		case MenuIds.AlternativeProducts:
		case MenuIds.Events:
		case MenuIds.None:
		case MenuIds.PhotoZoneDepth:
		case MenuIds.SymbolZoneDepth:
		case MenuIds.SymbolsList:
		case MenuIds.TextZoneDepth:
		case MenuIds.TextZoneAdd:
		case MenuIds.PhotoRotate: {
			return true; // log?
		}

		default: {
			// Can we do an exhaustiveness check here, so we get notified when a new menu is added
			return eXpress.utils.exhaustiveFail(menuId);
		}
	}
}

export function getDefaultMenuForZone(
	currentMenuId: MenuId | undefined,
	currentZone: eXpress.core.IZone | undefined,
	page: eXpress.core.IPageZone | null | undefined,
	dashboards: Parameters<typeof isSupportedMenu>[3],
	isTransformMode: boolean,
	/**
	 * Get default menu when a zone is clicked vs default menu for current state
	 * Can be used to detect zone change vs same zone is clicked \
	 * `null`: deselected a zone, non-zone selected \
	 * `undefined`: No zone clicked, like a undo state
	 */
	clickedZone: eXpress.core.IZone | null | undefined,
	/**
	 * Whether the desktop layout is active (sidebar).
	 * Desktop: first tap selects and immediately allows editing.
	 * Mobile: first tap selects, second tap enters edit mode, third tap exits edit mode.
	 */
	isDesktopLayout: boolean,
): MenuId {
	/**
	 * The new zone that will be active.
	 * The newly clicked zone or still selected zone
	 * undefined will be selected
	 */
	const newZone = clickedZone !== undefined ? clickedZone || undefined : currentZone;

	// QR zones
	if (
		eXpress.core.isPhotoZone(newZone) &&
		(newZone.photo?.photoSourceType === "qr" || newZone.defaultPhotoSourceType === "qr")
	) {
		switch (currentMenuId) {
			case MenuIds.Qr:
			case MenuIds.QrAdd:
			// @ts-expect-error TS7029: Fallthrough case in switch
			case MenuIds.QrEdit: {
				// Try to stay in the current menu
				if (isSupportedMenu(currentMenuId, newZone, page, dashboards)) {
					return currentMenuId;
				}
			}
			// eslint-disable-next-line no-fallthrough
			case MenuIds.AlternativeProducts:
			case MenuIds.BackgroundColor:
			case MenuIds.BackgroundPattern:
			case MenuIds.Backgrounds:
			case MenuIds.DateSelection:
			case MenuIds.Designs:
			case MenuIds.Events:
			case MenuIds.FontFamilies:
			case MenuIds.Layouts:
			case MenuIds.Map:
			case MenuIds.None:
			case MenuIds.Options:
			case MenuIds.Photo:
			case MenuIds.PhotoBrightness:
			case MenuIds.PhotoContrast:
			case MenuIds.PhotoEditing:
			case MenuIds.PhotoFilters:
			case MenuIds.PhotoRotate:
			case MenuIds.PhotoSaturation:
			case MenuIds.PhotoSelection:
			case MenuIds.PhotoTransform:
			case MenuIds.PhotoZoneDepth:
			case MenuIds.ProductOptions:
			case MenuIds.Root:
			case MenuIds.Symbols:
			case MenuIds.SymbolsList:
			case MenuIds.SymbolZoneDepth:
			case MenuIds.Text:
			case MenuIds.TextHAlignment:
			case MenuIds.TextVAlignment:
			case MenuIds.TextColor:
			case MenuIds.TextSize:
			case MenuIds.TextStyle:
			case MenuIds.TextValue:
			case MenuIds.TextZoneAdd:
			case MenuIds.TextZoneDepth:
				// Try to goto Qr Menu
				if (isSupportedMenu(MenuIds.Qr, newZone, page, dashboards)) {
					return MenuIds.Qr;
				}

				break;
			default:
				eXpress.utils.exhaustiveFail(currentMenuId);
		}

		// Try Root Menu
		if (isSupportedMenu(MenuIds.Root, newZone, page, dashboards)) {
			return MenuIds.Root;
		}

		return MenuIds.None;
	}

	// Map zones
	if (
		eXpress.core.isPhotoZone(newZone) &&
		(newZone.photo?.photoSourceType === "map" || newZone.defaultPhotoSourceType === "map")
	) {
		switch (currentMenuId) {
			// Because map has no menu, we allow to stay in none zone menus
			case MenuIds.AlternativeProducts:
			case MenuIds.BackgroundColor:
			case MenuIds.BackgroundPattern:
			case MenuIds.Backgrounds:
			case MenuIds.DateSelection:
			case MenuIds.Designs:
			case MenuIds.Events:
			case MenuIds.FontFamilies:
			case MenuIds.Layouts:
			case MenuIds.Map:
			// @ts-expect-error TS7029: Fallthrough case in switch
			case MenuIds.Options: {
				// Try to stay in the QR menu
				if (isSupportedMenu(currentMenuId, newZone, page, dashboards)) {
					return currentMenuId;
				}
			}

			// don't allow menus of other zone types
			// eslint-disable-next-line no-fallthrough
			case MenuIds.None:
			case MenuIds.Qr:
			case MenuIds.QrAdd:
			case MenuIds.QrEdit:
			case MenuIds.Photo:
			case MenuIds.PhotoBrightness:
			case MenuIds.PhotoContrast:
			case MenuIds.PhotoEditing:
			case MenuIds.PhotoFilters:
			case MenuIds.PhotoRotate:
			case MenuIds.PhotoSaturation:
			case MenuIds.PhotoSelection:
			case MenuIds.PhotoTransform:
			case MenuIds.PhotoZoneDepth:
			case MenuIds.ProductOptions:
			case MenuIds.Root:
			case MenuIds.Symbols:
			case MenuIds.SymbolsList:
			case MenuIds.SymbolZoneDepth:
			case MenuIds.Text:
			case MenuIds.TextHAlignment:
			case MenuIds.TextVAlignment:
			case MenuIds.TextColor:
			case MenuIds.TextSize:
			case MenuIds.TextStyle:
			case MenuIds.TextValue:
			case MenuIds.TextZoneAdd:
			case MenuIds.TextZoneDepth:
				// To Root
				break;
			default:
				eXpress.utils.exhaustiveFail(currentMenuId);
		}

		// Try Root Menu
		if (isSupportedMenu(MenuIds.Root, newZone, page, dashboards)) {
			return MenuIds.Root;
		}

		return MenuIds.None;
	}

	// Photo zones
	if (eXpress.core.isPhotoZone(newZone)) {
		// Goto Transform Menu?
		if (isTransformMode && !newZone.isEmpty) {
			if (isSupportedMenu(MenuIds.PhotoTransform, newZone, page, dashboards)) {
				return MenuIds.PhotoTransform;
			}
		}

		switch (currentMenuId) {
			// @ts-expect-error TS7029: Fallthrough case in switch
			case MenuIds.PhotoTransform: {
				const isSupported = isSupportedMenu(currentMenuId, newZone, page, dashboards);
				if (!isTransformMode || !isSupported || newZone.isEmpty) {
					if (isSupportedMenu(MenuIds.Photo, newZone, page, dashboards)) {
						return MenuIds.Photo;
					}
				}
			}
			// eslint-disable-next-line no-fallthrough
			case MenuIds.PhotoBrightness:
			case MenuIds.PhotoContrast:
			case MenuIds.PhotoEditing:
			case MenuIds.PhotoFilters:
			case MenuIds.PhotoRotate:
			case MenuIds.PhotoSaturation:
			case MenuIds.PhotoSelection:
			// @ts-expect-error TS7029: Fallthrough case in switch
			case MenuIds.PhotoZoneDepth: {
				// Try to stay in the photo menu
				if (isSupportedMenu(currentMenuId, newZone, page, dashboards)) {
					return currentMenuId;
				}
			}
			// eslint-disable-next-line no-fallthrough
			case MenuIds.AlternativeProducts:
			case MenuIds.BackgroundColor:
			case MenuIds.BackgroundPattern:
			case MenuIds.Backgrounds:
			case MenuIds.DateSelection:
			case MenuIds.Designs:
			case MenuIds.Events:
			case MenuIds.FontFamilies:
			case MenuIds.Layouts:
			case MenuIds.Map:
			case MenuIds.None:
			case MenuIds.Options:
			case MenuIds.ProductOptions:
			case MenuIds.Photo:
			case MenuIds.Qr:
			case MenuIds.QrAdd:
			case MenuIds.QrEdit:
			case MenuIds.Root:
			case MenuIds.Symbols:
			case MenuIds.SymbolsList:
			case MenuIds.SymbolZoneDepth:
			case MenuIds.Text:
			case MenuIds.TextHAlignment:
			case MenuIds.TextVAlignment:
			case MenuIds.TextColor:
			case MenuIds.TextSize:
			case MenuIds.TextStyle:
			case MenuIds.TextValue:
			case MenuIds.TextZoneAdd:
			case MenuIds.TextZoneDepth:
				// If an empty photo zone is selected, open only the photo-selection
				if (
					newZone.isEmpty &&
					isSupportedMenu(MenuIds.PhotoSelection, newZone, page, dashboards)
				) {
					return MenuIds.PhotoSelection;
				}

				// Try to goto Photo Menu
				if (isSupportedMenu(MenuIds.Photo, newZone, page, dashboards)) {
					return MenuIds.Photo;
				}
				break;
			default:
				eXpress.utils.exhaustiveFail(currentMenuId);
		}

		// Try Root Menu
		if (isSupportedMenu(MenuIds.Root, newZone, page, dashboards)) {
			return MenuIds.Root;
		}

		return MenuIds.None;
	}

	// If a text zone is selected, we try to go to a text menu (even if the opened menu can be used, example: templates)
	if (eXpress.core.isAnyTextZone(newZone)) {
		switch (currentMenuId) {
			case MenuIds.Text:
			case MenuIds.TextHAlignment:
			case MenuIds.TextVAlignment:
			case MenuIds.TextColor:
			case MenuIds.TextSize:
			case MenuIds.TextStyle:
			case MenuIds.TextValue:
			case MenuIds.TextZoneAdd:
			case MenuIds.FontFamilies:

			// @ts-expect-error TS7029: Fallthrough case in switch
			case MenuIds.TextZoneDepth: {
				// On mobile: 2nd tap on same zone → enter text edit mode (TextValue / OSK)
				// On desktop: same-zone tap does not toggle into TextValue; the sidebar textarea is always focused
				if (currentZone === clickedZone && !isDesktopLayout) {
					if (isSupportedMenu(MenuIds.TextValue, newZone, page, dashboards)) {
						return MenuIds.TextValue;
					}
				}
				// Try to stay in the same menu
				if (isSupportedMenu(currentMenuId, newZone, page, dashboards)) {
					return currentMenuId;
				}
				// If not allowed, fallback
			}
			// eslint-disable-next-line no-fallthrough
			case MenuIds.AlternativeProducts:
			case MenuIds.BackgroundColor:
			case MenuIds.BackgroundPattern:
			case MenuIds.Backgrounds:
			case MenuIds.DateSelection:
			case MenuIds.Designs:
			case MenuIds.Events:
			case MenuIds.Layouts:
			case MenuIds.Map:
			case MenuIds.None:
			case MenuIds.Options:
			case MenuIds.Photo:
			case MenuIds.PhotoBrightness:
			case MenuIds.PhotoContrast:
			case MenuIds.PhotoEditing:
			case MenuIds.PhotoFilters:
			case MenuIds.PhotoRotate:
			case MenuIds.PhotoSaturation:
			case MenuIds.PhotoSelection:
			case MenuIds.PhotoTransform:
			case MenuIds.PhotoZoneDepth:
			case MenuIds.ProductOptions:
			case MenuIds.Qr:
			case MenuIds.QrAdd:
			case MenuIds.QrEdit:
			case MenuIds.Root:
			case MenuIds.Symbols:
			case MenuIds.SymbolsList:
			case MenuIds.SymbolZoneDepth:
				// Try to goto Text Menu
				if (isSupportedMenu(MenuIds.Text, newZone, page, dashboards)) {
					return MenuIds.Text;
				}
				break;
			default:
				eXpress.utils.exhaustiveFail(currentMenuId);
		}

		// Try Root Menu
		if (isSupportedMenu(MenuIds.Root, newZone, page, dashboards)) {
			return MenuIds.Root;
		}

		return MenuIds.None;
	}

	if (eXpress.core.isSymbolZone(newZone)) {
		// If Symbols was already open try to reopen the same menu
		if (
			currentMenuId === MenuIds.Symbols &&
			isSupportedMenu(currentMenuId, newZone, page, dashboards)
		) {
			return currentMenuId;
		}
		// Try to open the symbols list menu
		if (isSupportedMenu(MenuIds.SymbolsList, newZone, page, dashboards)) {
			return MenuIds.SymbolsList;
		}
		// Open root symbol menu if not supported
		if (isSupportedMenu(MenuIds.Symbols, newZone, page, dashboards)) {
			return MenuIds.Symbols;
		}
	}

	// No zone selected
	if (newZone === undefined) {
		switch (currentMenuId) {
			case MenuIds.AlternativeProducts:
			case MenuIds.BackgroundColor:
			case MenuIds.BackgroundPattern:
			case MenuIds.Backgrounds:
			case MenuIds.DateSelection:
			case MenuIds.Designs:
			case MenuIds.Events:
			case MenuIds.FontFamilies:
			case MenuIds.Layouts:
			case MenuIds.None:
			case MenuIds.Options:
			case MenuIds.Root:
			case MenuIds.Text:
			case MenuIds.TextHAlignment:
			case MenuIds.TextVAlignment:
			case MenuIds.TextColor:
			case MenuIds.TextSize:
			case MenuIds.TextStyle:
			case MenuIds.TextValue:
			case MenuIds.TextZoneAdd:
			case MenuIds.TextZoneDepth:
			case MenuIds.Map:
			case MenuIds.Photo:
			case MenuIds.PhotoBrightness:
			case MenuIds.PhotoContrast:
			case MenuIds.PhotoEditing:
			case MenuIds.PhotoFilters:
			case MenuIds.PhotoRotate:
			case MenuIds.PhotoSaturation:
			case MenuIds.PhotoSelection:
			case MenuIds.PhotoTransform:
			case MenuIds.PhotoZoneDepth:
			case MenuIds.ProductOptions:
			case MenuIds.Qr:
			case MenuIds.QrAdd:
			case MenuIds.QrEdit:
			case MenuIds.Symbols:
			case MenuIds.SymbolsList:
			case MenuIds.SymbolZoneDepth: {
				// Try to keep menu open, except if now did deselect the zone
				if (clickedZone !== null) {
					// First try to keep the current above menus open (like a Backgrounds / Templates Panel)
					if (
						currentMenuId !== undefined &&
						isSupportedMenu(currentMenuId, newZone, page, dashboards)
					) {
						return currentMenuId;
					}
				}

				// Try Root Menu
				if (isSupportedMenu(MenuIds.Root, newZone, page, dashboards)) {
					return MenuIds.Root;
				}
				break;
			}
			default:
				eXpress.utils.exhaustiveFail(currentMenuId);
		}

		return MenuIds.None;
	}

	// Page selected?

	// First try to keep the current menu open (like a Backgrounds / Templates Panel)
	if (currentMenuId !== undefined && isSupportedMenu(currentMenuId, newZone, page, dashboards)) {
		return currentMenuId;
	}

	// Try Root Menu
	if (isSupportedMenu(MenuIds.Root, newZone, page, dashboards)) {
		return MenuIds.Root;
	}

	// No menu.NoMenu
	return MenuIds.None;
}
